import clsx from 'clsx';
import { lazy, memo, ReactNode, Suspense, useEffect, useRef } from 'react';
import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableRowProps
} from '@material-ui/core';
import { useInView } from 'react-intersection-observer';
import { SPaginatedTableProps } from '../../paginated';
import { useVirtualTableStyles } from './style';
import TableHeader, {
  tableCheckboxStyle,
  TableHeaderProps
} from '../../components/table/header';
import { RowContainer } from '../../components/row-container';
import { BROWSERS, useBrowser } from '../../../../hooks';
import { SInfiniteScrollProps } from '../../../sinfinite-scroll';
import { SCheckbox, SCheckboxProps } from '../../../scheckbox';
import TableHeaderCell from '../../styles/TableHeaderCell';
import { SRadio } from '../../../sradio';

const LazyTableBodyCell = lazy(
  () => import('@setvi/shared/components/s-table/components/table/cells')
);

// Stable identity so a memoized TableHeader isn't re-rendered by a fresh
// style object every time the table renders (which is every scroll frame).
const HEADER_ROW_STYLE = { display: 'flex' } as const;

interface VirtualRow<T> extends Omit<TableRowProps, 'onClick'> {
  rowStyleGetter?: (row: T, index?: number) => React.CSSProperties;
  cellStyleGetter?: (row: T, index?: number) => React.CSSProperties;
  checkbox?: (row: T) => SCheckboxProps;
  onClick?: (row: T) => void;
  additionalContent?: (row: T) => JSX.Element;
  fallback?: ReactNode;
}

export interface SVirtualInfiniteScrollProps<T> extends Omit<
  SPaginatedTableProps<T>,
  'pagination' | 'showPagination' | 'customEmptyState' | 'tableStyle' | 'row'
> {
  id?: string;
  header?: Omit<Partial<TableHeaderProps>, 'checkboxFreeSpace'>;
  vList: VirtualItem[];
  height: number;
  infiniteScroll?: Omit<
    SInfiniteScrollProps<T>,
    | 'containerComponent'
    | 'wrapperComponent'
    | 'onWrapperClick'
    | 'list'
    | 'keyGetter'
    | 'render'
    | 'children'
  >;
  row?: VirtualRow<T>;
  options?: Record<string, string | number>;
  // Color even rows
  stripped?: boolean;
  // ! This is temp prop for distinguishing between the new and old design
  newDesign?: boolean;
  type?: 'none' | 'checkbox' | 'radio';
  showEmptyMessage?: boolean;
  footer?: JSX.Element;
  /**
   * Pass the virtualizer that produced `vList` to size rows by measuring
   * their rendered content instead of fixing them at the estimate — for lists
   * whose row height varies with the data or the viewport. The estimate then
   * only positions rows until they are first measured.
   */
  virtualizer?: Virtualizer<any, any>;
}

interface VirtualTableRowProps<T> {
  // Scalars instead of the VirtualItem: measuring any row gives every item
  // after it a new object identity, so memoizing on values is what lets rows
  // whose position didn't actually change skip the re-render.
  rowIndex: number;
  rowStart: number;
  rowSize: number;
  element: T;
  /**
   * Index handed to `rowStyleGetter`/`cellStyleGetter`: the row's absolute
   * list index when measuring (`virtualizer` set), the position within the
   * rendered slice otherwise (the historical behavior).
   */
  styleIndex: number;
  isLast: boolean;
  isLoaderRow: boolean;
  /** The `useInView` ref — the last row doubles as the fetch-more sentinel. */
  sentinelRef: (node?: Element | null) => void;
  virtualizer?: Virtualizer<any, any>;
  rowClassName: string;
  scrollMargin: number;
  stripped?: boolean;
  newDesign?: boolean;
  row: VirtualRow<T>;
  type: 'none' | 'checkbox' | 'radio';
  checkboxFreeSpace: boolean;
  columns: SPaginatedTableProps<T>['columns'];
  isLoading?: boolean;
  searchQuery?: string;
  onWrapperDrag?: (element: T) => void;
  onWrapperDrop?: (element: T) => void;
  renderDragItem?: (element: T) => Element;
}

const useMeasureRowRef = (
  virtualizer: Virtualizer<any, any> | undefined,
  isLast: boolean,
  sentinelRef: (node?: Element | null) => void
) => {
  // Measure a node only when it first attaches: measureElement subscribes a
  // ResizeObserver that reports any later size change, so re-measuring on
  // every commit would only force synchronous layout for nothing.
  const measuredNode = useRef<HTMLTableRowElement | null>(null);

  return (node: HTMLTableRowElement | null) => {
    if (virtualizer && node && node !== measuredNode.current) {
      measuredNode.current = node;
      virtualizer.measureElement(node);
    }
    if (isLast) sentinelRef(node);
  };
};

const VirtualTableRowInner = <T,>({
  rowIndex,
  rowStart,
  rowSize,
  element,
  styleIndex,
  isLast,
  isLoaderRow,
  sentinelRef,
  virtualizer,
  rowClassName,
  scrollMargin,
  stripped,
  newDesign,
  row,
  type,
  checkboxFreeSpace,
  columns,
  isLoading,
  searchQuery,
  onWrapperDrag,
  onWrapperDrop,
  renderDragItem
}: VirtualTableRowProps<T>) => {
  const {
    fallback,
    checkbox,
    onClick,
    additionalContent,
    rowStyleGetter,
    cellStyleGetter
  } = row;

  const measureRef = useMeasureRowRef(virtualizer, isLast, sentinelRef);
  // One call per render — the getter typically scans the selection, so asking
  // once instead of per prop matters at scroll-burst row counts.
  const checkboxProps = checkbox?.(element);

  return (
    <TableRow
      // measureElement reads the item index off this attribute.
      data-index={rowIndex}
      onClick={() => onClick?.(element)}
      draggable={!!onWrapperDrag}
      onDragStart={() => onWrapperDrag?.(element)}
      className={rowClassName}
      onDragOver={e => e.preventDefault()}
      onDragStartCapture={e => {
        const div = renderDragItem?.(element);
        if (div) e.dataTransfer.setDragImage(div, 0, 0);
      }}
      onDrop={ev => {
        ev.preventDefault();
        ev.stopPropagation();
        onWrapperDrop?.(element);
      }}
      style={{
        backgroundColor: '#fff',
        // Measured rows size themselves to their content; the virtualizer
        // repositions the ones below from the measurement.
        ...(virtualizer ? {} : { height: `${rowSize}px` }),
        transform: `translateY(${rowStart - scrollMargin}px) translateX(0px)`,
        borderBottom: '1px solid #E6E7E9',

        ...(stripped
          ? {
              backgroundColor: rowIndex % 2 !== 0 && '#F8F9FA',
              borderBottom: 'none',
              borderRadius: 12
            }
          : {}),

        ...rowStyleGetter?.(element, styleIndex)
      }}
      ref={measureRef}>
      {isLoaderRow ? (
        'Loading more...'
      ) : (
        <>
          <Suspense fallback={fallback}>
            <RowContainer
              checkboxFreeSpace={checkboxFreeSpace}
              additionalContent={additionalContent?.(element)}>
              <>
                {checkbox && (
                  <TableHeaderCell
                    style={{
                      ...cellStyleGetter?.(element, styleIndex),
                      ...tableCheckboxStyle
                    }}
                    onClick={(e: any) => e.stopPropagation()}>
                    {type === 'checkbox' && (
                      <SCheckbox
                        onChange={e => {
                          e.stopPropagation();
                          checkboxProps?.onChange?.(e, e?.target?.checked);
                        }}
                        checked={checkboxProps?.checked}
                        disabled={checkboxProps?.disabled}
                        tooltip={checkboxProps?.tooltip}
                        {...checkbox}
                      />
                    )}
                    {type === 'radio' && (
                      <SRadio
                        newDesign={newDesign}
                        name="radio-button-product"
                        onChange={e => {
                          e.stopPropagation();
                          checkboxProps?.onChange?.(e, e?.target?.checked);
                        }}
                        checked={checkboxProps?.checked}
                        disabled={checkboxProps?.disabled}
                        tooltip={checkboxProps?.tooltip}
                        {...checkbox}
                      />
                    )}
                  </TableHeaderCell>
                )}
                <LazyTableBodyCell
                  rowData={element}
                  columns={columns}
                  loading={isLoading}
                  searchQuery={searchQuery}
                  style={rowStyleGetter?.(element, styleIndex)}
                />
              </>
            </RowContainer>
          </Suspense>
        </>
      )}
    </TableRow>
  );
};

/**
 * Memoized with the default shallow compare: a row re-renders only when one of
 * its props changes. Consumers that keep `row`, `columns` and the list
 * elements referentially stable (and route row state like selection through
 * them) therefore pay only for newly mounted rows while scrolling; consumers
 * that rebuild those props every render keep today's render-everything
 * behavior.
 */
const VirtualTableRow = memo(
  VirtualTableRowInner
) as typeof VirtualTableRowInner;

const VirtualTable = <T,>({
  id,
  list,
  row = {},
  vList,
  type = 'checkbox',
  header,
  height,
  options,
  columns,
  isLoading,
  searchQuery,
  stickyHeader,
  infiniteScroll = {},
  stripped,
  footer,
  newDesign = false,
  showEmptyMessage = true,
  keyGetter,
  virtualizer
}: SVirtualInfiniteScrollProps<T>) => {
  const { browser } = useBrowser();
  const isSafari = browser === BROWSERS.safari;
  const {
    wrapperClass,
    containerClass,
    options: intersectionOptions,
    fetchMore,
    onWrapperDrop,
    onWrapperDrag,
    renderDragItem
  } = infiniteScroll;
  const classes = useVirtualTableStyles({
    wrapperClick: !!row?.onClick,
    height,
    isSafari,
    newDesign,
    stickyHeader: !!stickyHeader
  });
  const { ref, inView } = useInView(intersectionOptions);
  const checkboxFreeSpace = !header?.checkbox && !!row?.checkbox;
  const rowClassName = clsx(
    classes.wrapper,
    wrapperClass,
    newDesign && classes.newDesignWrapper
  );

  useEffect(() => {
    if (inView) {
      fetchMore?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <Table stickyHeader={stickyHeader} className={classes.table}>
      <TableHeader
        newDesign={newDesign}
        columns={header?.columns || columns}
        checkboxFreeSpace={checkboxFreeSpace}
        rowStyle={HEADER_ROW_STYLE}
        {...header}
      />

      <TableBody id={id} className={clsx(classes.tbody, containerClass)}>
        {vList.length === 0 && !isLoading && showEmptyMessage && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}>
            No data found
          </Box>
        )}

        {vList.map((virtualRow, index) => {
          const isLoaderRow = virtualRow.index > list?.length - 1;
          const element = list[virtualRow.index];

          if (!element) return null;

          return (
            <VirtualTableRow
              key={keyGetter(element)}
              rowIndex={virtualRow.index}
              rowStart={virtualRow.start}
              rowSize={virtualRow.size}
              element={element}
              styleIndex={virtualizer ? virtualRow.index : index}
              isLast={virtualRow?.index + 1 === list?.length}
              isLoaderRow={isLoaderRow}
              sentinelRef={ref}
              virtualizer={virtualizer}
              rowClassName={rowClassName}
              scrollMargin={(options?.scrollMargin as number) || 0}
              stripped={stripped}
              newDesign={newDesign}
              row={row}
              type={type}
              checkboxFreeSpace={checkboxFreeSpace}
              columns={columns}
              isLoading={isLoading}
              searchQuery={searchQuery}
              onWrapperDrag={onWrapperDrag}
              onWrapperDrop={onWrapperDrop}
              renderDragItem={renderDragItem}
            />
          );
        })}

        {!!footer && footer}
      </TableBody>
    </Table>
  );
};

const SVirtualTable = memo(VirtualTable) as typeof VirtualTable;
export default SVirtualTable;
